/**
 * Members - FR-8.3's search surface (spec 03).
 *
 * Searching matches a name, an email, or a mobile number in any formatting: the
 * API reduces the term to digits for the mobile match (`account_service.
 * search_digits`), so an admin can paste "980-000-0001" or "+977 9800000001" and
 * still find the member. Nothing is normalised here - sending the raw term keeps
 * that rule in the one place that owns it.
 *
 * The status filter is the same one the dashboard's "active members" tile counts,
 * so the number in the tile and the `total` below it always agree. The
 * `?status=` query parameter is readable from the URL, which is what makes the
 * dashboard tile a link into this exact filtered view.
 *
 * Members who deleted their own account (spec 19) are listed only under the
 * `deleted` filter: the server leaves them out of "All".
 *
 * "Add member" (spec 17) is for people who already belong to the gym: the front
 * desk has every detail, so the account is created active - no request, no
 * queue.
 */

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  Pagination,
  Select,
  statusTone,
} from "../components/primitives";
import {
  createMember,
  fetchMembers,
  GENDERS,
  memberPath,
  type Gender,
  type MemberCreate,
  type MemberListItem,
  type MemberOutcome,
} from "../lib/api/accounts";
import { ApiError } from "../lib/api/errors";
import type { MemberStatus, Page as PageType } from "../lib/api/types";
import { useMutation, useQuery } from "../lib/hooks";
import {
  formatDate,
  humanizeStatus,
  kathmanduYearsAgo,
  MAX_MEMBER_AGE,
  MIN_MEMBER_AGE,
  relativeDay,
} from "../lib/format";

const PAGE_SIZE = 50;
const STATUSES: MemberStatus[] = ["pending", "active", "suspended", "rejected", "deleted"];

export function MembersScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  // The search box is local state so typing does not fire a request per
  // keystroke; the committed term lives in the URL, so a filtered list is
  // linkable and a refresh keeps it.
  const committedQ = params.get("q") ?? "";
  const [term, setTerm] = useState(committedQ);
  const status = (params.get("status") ?? "") as MemberStatus | "";
  const createdFrom = params.get("created_from") ?? "";
  const createdTo = params.get("created_to") ?? "";
  const offset = Number(params.get("offset") ?? 0) || 0;

  function apply(changes: Record<string, string | number | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    }
    if (!("offset" in changes)) next.delete("offset");
    setParams(next, { replace: true });
  }

  const query = useQuery<PageType<MemberListItem>>(
    () =>
      fetchMembers({
        q: committedQ || undefined,
        status: status || undefined,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [committedQ, status, createdFrom, createdTo, offset],
  );
  const { data, error, loading, refreshing, reload } = query;

  const filtersActive = Boolean(committedQ || status || createdFrom || createdTo);

  const columns: Array<Column<MemberListItem>> = [
    {
      key: "name",
      header: "Member",
      render: (row) => (
        <PrimaryCell
          title={
            <>
              {row.full_name}
              {/* FR-8.2's analytics exclude demo accounts, and the flag is here so
                  an admin reading a count can see which rows are fabricated. */}
              {row.is_demo_account ? (
                <>
                  {" "}
                  <Badge tone="plain">demo</Badge>
                </>
              ) : null}
            </>
          }
          detail={row.email ?? "No email on file"}
        />
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      render: (row) => <span className="mono">{row.mobile_number}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <Badge tone={statusTone(row.status)}>{row.status}</Badge>,
    },
    {
      key: "joined",
      header: "Registered",
      render: (row) => (
        <PrimaryCell title={formatDate(row.created_at)} detail={relativeDay(row.created_at)} />
      ),
    },
    {
      key: "login",
      header: "Last sign-in",
      render: (row) =>
        row.last_login_at ? (
          formatDate(row.last_login_at)
        ) : (
          <span className="text-meta">Never</span>
        ),
    },
    {
      key: "open",
      header: "",
      align: "right",
      render: (row) => (
        <Link to={memberPath(row.id)} className="btn btn--ghost btn--sm">
          Open
        </Link>
      ),
    },
  ];

  return (
    <Page>
      <Card>
        <form
          className="filters"
          onSubmit={(event) => {
            event.preventDefault();
            apply({ q: term.trim() });
          }}
        >
          <div style={{ width: 280 }}>
            <Field label="Search">
              {(id) => (
                <Input
                  id={id}
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="Name, email, or mobile number"
                  maxLength={254}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={status}
                  onChange={(event) => apply({ status: event.target.value })}
                >
                  <option value="">All</option>
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="Registered from">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={createdFrom}
                  onChange={(event) => apply({ created_from: event.target.value })}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="Registered to">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={createdTo}
                  onChange={(event) => apply({ created_to: event.target.value })}
                />
              )}
            </Field>
          </div>

          <div className="row row--tight">
            <Button type="submit" variant="primary">
              Search
            </Button>
            <Button
              onClick={() => {
                setTerm("");
                apply({ q: null, status: null, created_from: null, created_to: null });
              }}
              disabled={!filtersActive && !term}
            >
              Clear
            </Button>
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="accent" onClick={() => setCreating(true)}>
              Add member
            </Button>
          </div>
        </form>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={6} columns={6} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No members match these filters."
            body="Try part of a name, or clear the filters. A gym member who is not in the system yet can be added directly."
            action={
              <Button variant="accent" onClick={() => setCreating(true)}>
                Add member
              </Button>
            }
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(row) => row.id} />
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="members"
              onChange={(next) => apply({ offset: next })}
            />
          </>
        )}
      </Card>

      {creating ? (
        <CreateMemberDialog
          onClose={() => setCreating(false)}
          onDone={(result) => {
            setCreating(false);
            // Land on the member's page: that is where the next step (a
            // membership record) starts.
            navigate(memberPath(result.member.id), { state: { saved: result.message } });
          }}
        />
      ) : null}
    </Page>
  );
}

// --- Add member (spec 17) ----------------------------------------------------

/** Which input a server refusal belongs under. 422s name the field themselves;
 *  the two 409s do not, but each is about exactly one input. */
function fieldOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.field) return error.field;
  if (error.code === "mobile_number_taken") return "mobile_number";
  if (error.code === "email_taken") return "email";
  return null;
}

function CreateMemberDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (result: MemberOutcome) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [referredBy, setReferredBy] = useState("");
  // The same default the member app's register form uses.
  const [country, setCountry] = useState("Nepal");
  const [city, setCity] = useState("");
  const [areaId, setAreaId] = useState("");

  // Latest DOB is today minus the minimum age; the API enforces the same rule.
  const latestDob = kathmanduYearsAgo(MIN_MEMBER_AGE);
  const earliestDob = kathmanduYearsAgo(MAX_MEMBER_AGE);

  const mutation = useMutation(
    () => {
      const payload: MemberCreate = {
        full_name: fullName.trim(),
        mobile_number: mobile.trim(),
        // Not trimmed: a leading or trailing space is part of the password,
        // and the register form does not trim either.
        password,
      };
      // Only send what was filled in; the server would null a "" anyway, but
      // absent is the honest wire form.
      if (email.trim()) payload.email = email.trim();
      if (dateOfBirth) payload.date_of_birth = dateOfBirth;
      if (gender) payload.gender = gender;
      if (referredBy.trim()) payload.referred_by = referredBy.trim();
      if (country.trim()) payload.country = country.trim();
      if (city.trim()) payload.city = city.trim();
      if (areaId.trim()) payload.area_id = areaId.trim();
      return createMember(payload);
    },
    { onSuccess: (result) => onDone(result) },
  );

  const errorField = fieldOf(mutation.error);
  const errorFor = (name: keyof MemberCreate) =>
    errorField === name && mutation.error instanceof ApiError ? mutation.error.message : null;

  const blocked = !fullName.trim() || !mobile.trim() || password.length < 8;

  return (
    <Modal
      title="Add member"
      description="For members who already belong to the gym. The account is active immediately, the same as an approval."
      onClose={onClose}
      wide
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Full name (required)" error={errorFor("full_name")}>
            {(id) => (
              <Input
                id={id}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                autoComplete="off"
                maxLength={120}
              />
            )}
          </Field>

          <Field
            label="Mobile number (required)"
            hint="10 digits starting with 9. This is what the member signs in with."
            error={errorFor("mobile_number")}
          >
            {(id) => (
              <Input
                id={id}
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="98XXXXXXXX"
                inputMode="tel"
                autoComplete="off"
                maxLength={32}
              />
            )}
          </Field>

          <div className="form-grid__wide">
            <Field
              label="Password (required)"
              hint="At least 8 characters. Tell the member this password; they can change it from the app."
              error={errorFor("password")}
            >
              {(id) => (
                <div className="row row--tight">
                  <Input
                    id={id}
                    type={reveal ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    maxLength={200}
                    style={{ flex: 1 }}
                  />
                  <Button size="sm" variant="quiet" onClick={() => setReveal((v) => !v)}>
                    {reveal ? "Hide" : "Show"}
                  </Button>
                </div>
              )}
            </Field>
          </div>

          <Field label="Email" hint="Optional." error={errorFor("email")}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="off"
                maxLength={254}
              />
            )}
          </Field>

          <Field label="Date of birth" error={errorFor("date_of_birth")}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={dateOfBirth}
                min={earliestDob}
                max={latestDob}
                onChange={(event) => setDateOfBirth(event.target.value)}
              />
            )}
          </Field>

          <Field label="Gender" error={errorFor("gender")}>
            {(id) => (
              <Select id={id} value={gender} onChange={(event) => setGender(event.target.value as Gender | "")}>
                <option value="">Not recorded</option>
                {GENDERS.map((value) => (
                  <option key={value} value={value}>
                    {humanizeStatus(value)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Referred by" hint="The member who referred them, if any." error={errorFor("referred_by")}>
            {(id) => (
              <Input
                id={id}
                value={referredBy}
                onChange={(event) => setReferredBy(event.target.value)}
                maxLength={120}
              />
            )}
          </Field>

          <Field label="Country" error={errorFor("country")}>
            {(id) => (
              <Input id={id} value={country} onChange={(event) => setCountry(event.target.value)} maxLength={120} />
            )}
          </Field>

          <Field label="City" error={errorFor("city")}>
            {(id) => <Input id={id} value={city} onChange={(event) => setCity(event.target.value)} maxLength={120} />}
          </Field>

          <Field label="Area ID" hint="e.g. Budhanilkantha" error={errorFor("area_id")}>
            {(id) => (
              <Input id={id} value={areaId} onChange={(event) => setAreaId(event.target.value)} maxLength={120} />
            )}
          </Field>
        </div>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button variant="accent" onClick={() => void mutation.run()} disabled={blocked || mutation.pending}>
            {mutation.pending ? "Creating…" : "Create member"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
